import React from 'react'
import { Button, Checkbox } from 'antd'
import { Flex } from 'antd-mobile'
import { connect } from 'react-redux'
import { handleAmount, intl, yearlyRateToMonthlyPercent } from 'utils'
import classes from './styles.module.scss'
import messages from './messages'
import WhiteSpace from 'components/WhiteSpace'
import _ from 'lodash'

class VisaInstallments extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedPlan: null, // null for full payment, or installment plan index
      expandedPlan: null, // which plan is expanded to show description
      checkedPlans: {}, // Track which plans have checkbox checked
      fullPaymentSelected: false, // Track if full payment is selected
    }
  }

  handlePlanClick = (planIndex) => {
    // Toggle expansion when clicking on plan
    this.setState({
      expandedPlan: this.state.expandedPlan === planIndex ? null : planIndex,
      fullPaymentSelected: false,
    })
  }

  handleCheckboxChange = (planIndex, e) => {
    const { checked } = e.target
    this.setState((prevState) => {
      const newCheckedPlans = { ...prevState.checkedPlans }
      if (checked) {
        newCheckedPlans[planIndex] = true
        // Uncheck other plans
        Object.keys(newCheckedPlans).forEach((key) => {
          if (key !== planIndex.toString()) {
            delete newCheckedPlans[key]
          }
        })
        return {
          checkedPlans: newCheckedPlans,
          selectedPlan: planIndex,
          fullPaymentSelected: false,
        }
      } else {
        delete newCheckedPlans[planIndex]
        return {
          checkedPlans: newCheckedPlans,
          selectedPlan: null,
          fullPaymentSelected: false,
        }
      }
    })
  }

  handleFullPaymentSelect = () => {
    this.setState({
      selectedPlan: null,
      expandedPlan: null,
      checkedPlans: {},
      fullPaymentSelected: true,
    })
  }

  handlePay = () => {
    const { onPay } = this.props
    const { selectedPlan } = this.state
    const installmentPlans = this.getSortedInstallmentPlans()

    if (selectedPlan === null) {
      // Full payment
      onPay({
        installmentPlanId: null,
        installmentPlan: null,
      })
    } else {
      // Installment plan selected
      const selectedInstallmentPlan = installmentPlans[selectedPlan]
      onPay({
        installmentPlanId: selectedInstallmentPlan.installmentPlanId,
        installmentPlan: selectedInstallmentPlan,
      })
    }
  }

  getPayAmount = () => {
    const { siteConfig } = this.props
    const { selectedPlan } = this.state
    const installmentPlans = this.getSortedInstallmentPlans()
    const payAmount = _.get(siteConfig, 'payInfo.payAmount')

    if (selectedPlan !== null && installmentPlans[selectedPlan]) {
      const selectedInstallmentPlan = installmentPlans[selectedPlan]
      if (selectedInstallmentPlan.totalInstallmentAmount) {
        return selectedInstallmentPlan.totalInstallmentAmount
      }
    }

    return payAmount
  }

  getSortedInstallmentPlans = () => {
    const { installmentPlans } = this.props
    return _.sortBy(installmentPlans || [], 'installmentNumber')
  }

  formatInstallmentFrequency = (frequency) => {
    if (!frequency) return ''
    const freqMap = {
      MONTHS: 'month',
      MONTH: 'month',
      month: 'month',
    }
    return freqMap[frequency.toUpperCase()] || frequency.toLowerCase()
  }

  formatAmountWithAedFont = (amountData) => {
    // handleAmount now automatically replaces AED with the symbol
    return handleAmount(amountData)
  }

  formatPercent = (value) => {
    const n = Number(value)
    if (!Number.isFinite(n)) return '0%'
    return `${parseFloat(n.toFixed(2))}%`
  }

  render() {
    const { siteConfig, loading } = this.props
    const installmentPlans = this.getSortedInstallmentPlans()
    const { selectedPlan, expandedPlan } = this.state
    const payAmount = _.get(siteConfig, 'payInfo.payAmount')

    if (!installmentPlans || installmentPlans.length === 0) {
      return null
    }

    const isPayButtonDisabled = !this.state.fullPaymentSelected && selectedPlan === null && Object.keys(this.state.checkedPlans).length === 0

    return (
      <div className={classes.visaInstallments}>
        <WhiteSpace size='lg' />

        {/* Title */}
        <h2 className={classes.pageTitle}>Confirm the payment</h2>

        <WhiteSpace size='md' />

        {/* Full Payment Option */}
        <div
          className={`${classes.paymentOption} ${this.state.fullPaymentSelected ? classes.selected : ''}`}
          onClick={this.handleFullPaymentSelect}
        >
          <Flex justify='between' align='center'>
            <div>
              <div className={classes.amount}>{this.formatAmountWithAedFont(payAmount)}</div>
              <div className={classes.label}>Full Payment</div>
            </div>
            {this.state.fullPaymentSelected && (
              <span className='iconfont iconcomm-choose green font-24' />
            )}
          </Flex>
        </div>

        <WhiteSpace size='md' />

        {/* Installments Enabled by VISA */}
        <div className={classes.installmentsHeader}>
          <img
            src={require('./images/visa_watermark.png')}
            alt='VISA'
            className={classes.visaLogo}
          />
        </div>

        <WhiteSpace size='md' />

        {/* Installment Plans */}
        {installmentPlans.map((plan, planIndex) => {
          const isSelected = selectedPlan === planIndex
          const isExpanded = expandedPlan === planIndex
          const isChecked = this.state.checkedPlans[planIndex] || false
          const monthlyAmount = plan.firstInstallmentAmount
          const months = plan.installmentNumber
          const frequency = this.formatInstallmentFrequency(plan.installmentFrequencySingle || plan.installmentFrequency)

          return (
            <div key={`${plan.installmentPlanId}-${planIndex}`}>
              <div
                className={`${classes.installmentOption} ${isSelected ? classes.selected : ''} ${isExpanded ? classes.expanded : ''}`}
                onClick={() => this.handlePlanClick(planIndex)}
              >
                <Flex justify='between' align='center'>
                  <div className={classes.installmentInfo}>
                    <div className={classes.amountRow}>
                      <div className={classes.amount}>
                        {this.formatAmountWithAedFont(monthlyAmount)}/{frequency}
                      </div>
                      <span className={`${classes.monthBadge} ${isSelected ? classes.selectedBadge : ''}`}>
                        {months} {frequency === 'month' ? 'Months' : frequency}
                      </span>
                    </div>
                    <div className={classes.planDetails}>
                      <div className={classes.fees}>
                        Processing Fee: {handleAmount(plan.totalUpfrontFees || { amount: 0, currency: monthlyAmount?.currency })} |
                        Monthly Rate:{' '}
                        {this.formatPercent(
                          yearlyRateToMonthlyPercent(plan.yearlyRate ?? plan.annualPercentageRate)
                        )}
                      </div>
                    </div>
                  </div>
                </Flex>

                {/* Expanded Description - Inside the card */}
                {isExpanded && (
                  <div className={classes.expandedDescription}>
                    <WhiteSpace size='sm' />
                    <div className={classes.descriptionContent}>
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation()
                          this.handleCheckboxChange(planIndex, e)
                        }}
                        className={classes.descriptionCheckbox}
                      />
                      <div className={classes.descriptionText}>
                        {plan.installmentTAndCContent ? (
                          <div style={{ whiteSpace: 'pre-line' }}>
                            {plan.installmentTAndCContent}
                          </div>
                        ) : (
                          `You are selecting a ${months}-${frequency} installment plan. The full purchase amount will be deducted from your available credit limit. If you make all payments on time, no fees or interest will apply. If you miss a payment, your card's standard APR will apply to the overdue amount.`
                        )}
                      </div>
                    </div>
                    <WhiteSpace size='sm' />
                  </div>
                )}
              </div>

              <WhiteSpace size='sm' />
            </div>
          )
        })}

        <WhiteSpace size='lg' />

        {/* Pay Button */}
        <Button
          loading={loading}
          block
          size='large'
          type='primary'
          onClick={this.handlePay}
          disabled={isPayButtonDisabled}
          className={classes.payButton}
        >
          {intl().formatMessage(messages.Pay)} {handleAmount(this.getPayAmount())}
        </Button>
      </div>
    )
  }
}

export default connect(
  (state) => ({
    siteConfig: state.siteConfig,
  }),
  {}
)(VisaInstallments)
